import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Spinner, Card, Table } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { Get, Post, Put, DeleteSingle as Delete } from '../../../../utils/httpProvider';
import { DIEUKIEN_QUYETDINH_API, KIEMTRASOLIEU_API, BaseGatewayImage } from '../../../../utils/apiUrl';
import { objectLoaiDieuKien, objectLoaiDanhGia, trangThaiGuiKetQuaValue } from '../../../../utils/define';
import DocumentInput from '../../../../components/document-input/DocumentInput';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';

const FieldDisplayFile = ({ label, value }) => {
    const getSafeUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
        const safePath = path.startsWith('/') ? path : `/${path}`;
        return `${baseUrl}${safePath}`;
    };

    let files = [];
    if (value) {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                files = parsed;
            } else {
                files = [{ filePath: value, name: value.split('/').pop() }];
            }
        } catch (e) {
            files = [{ filePath: value, name: value.split('/').pop() }];
        }
    }

    return (
        <Form.Group className="mb-3">
            {label && <Form.Label className="font-weight-bold">{label}</Form.Label>}
            <div className="d-flex flex-wrap">
                {files.length > 0 ? (
                    files.map((file, idx) => {
                        const path = file.filePath || file;
                        let name = file.name || (typeof path === 'string' ? path.split('/').pop() : 'File');
                        name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                        return (
                            <a key={idx} href={getSafeUrl(path)} target="_blank" rel="noreferrer" className={`btn btn-outline-primary text-left ${label ? '' : 'btn-sm'} mb-1 mr-1`} style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                                <i className="fas fa-paperclip mr-1"></i> {name}
                            </a>
                        );
                    })
                ) : (
                    <span className="text-muted small">Không có file</span>
                )}
            </div>
        </Form.Group>
    );
};

const DieuKienQuyetDinhForm = ({ kiemTraSoLieuId, kiemTraSoLieuData, readOnly = false, isUserView = false, refetchParent = () => {}, onDataLoaded, onSaveSuccess }) => {
    const [saving, setSaving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    
    const [fileQuyetDinh, setFileQuyetDinh] = useState('');
    const [chiTiets, setChiTiets] = useState([]);

    const { data: queryRes, refetch: refetchChiTiets, isLoading } = useQuery(
        ['dieuKienQuyetDinhData', kiemTraSoLieuId], 
        () => Get(DIEUKIEN_QUYETDINH_API.GET_BY_KIEMTRA_ID, kiemTraSoLieuId),
        { 
            enabled: !!kiemTraSoLieuId,
            refetchOnWindowFocus: false 
        }
    );

    useEffect(() => {
        if (queryRes) {
            let list = [];
            if (Array.isArray(queryRes)) {
                list = queryRes;
            } else if (queryRes.data && Array.isArray(queryRes.data)) {
                list = queryRes.data;
            } else if (queryRes.data?.$values) {
                list = queryRes.data.$values;
            }

            if (list && list.length > 0) {
                const initializedList = objectLoaiDieuKien.map(def => {
                    const existing = list.find(item => item.loaiDieuKien === def.value);
                    if (existing) return existing;
                    return { id: null, loaiDieuKien: def.value, loaiDanhGia: null, fileQuyetDinh: '' };
                });

                setChiTiets(initializedList);
                
                const itemWithFile = list.find(item => item.fileQuyetDinh);
                if (itemWithFile) {
                    setFileQuyetDinh(itemWithFile.fileQuyetDinh);
                }
                if (onDataLoaded) onDataLoaded(true);
            } else {
                const defaultList = objectLoaiDieuKien.map(item => ({
                    id: null,
                    loaiDieuKien: item.value,
                    loaiDanhGia: null,
                    fileQuyetDinh: ''
                }));
                setChiTiets(defaultList);
                if (onDataLoaded) onDataLoaded(false);
            }
        } else if (queryRes === null) {
            const defaultList = objectLoaiDieuKien.map(item => ({
                id: null,
                loaiDieuKien: item.value,
                loaiDanhGia: null,
                fileQuyetDinh: ''
            }));
            setChiTiets(defaultList);
            if (onDataLoaded) onDataLoaded(false);
        }
    }, [queryRes]);

    const handleChiTietChange = (index, field, value) => {
        const newChiTiets = [...chiTiets];
        newChiTiets[index][field] = value;
        setChiTiets(newChiTiets);
    };

    const handleSave = async (isSend = false) => {
        for (let i = 0; i < chiTiets.length; i++) {
            if (!chiTiets[i].loaiDanhGia) {
                toast.error(`Vui lòng chọn Đánh giá cho điều kiện: ${objectLoaiDieuKien.find(x => x.value === chiTiets[i].loaiDieuKien)?.label}`);
                return;
            }
        }
        
        if (isSend && !fileQuyetDinh) {
            toast.error('Vui lòng gắn File quyết định trước khi gửi!');
            return;
        }

        setSaving(true);
        try {
            for (const ct of chiTiets) {
                const ctData = {
                    KiemTraSoLieuId: kiemTraSoLieuId,
                    LoaiDieuKien: ct.loaiDieuKien,
                    LoaiDanhGia: ct.loaiDanhGia,
                    FileQuyetDinh: fileQuyetDinh || ''
                };

                if (ct.id) {
                    await Put(DIEUKIEN_QUYETDINH_API.UPDATE_ITEM, ct.id, { ...ctData, Id: ct.id });
                } else {
                    await Post(DIEUKIEN_QUYETDINH_API.CREATE, null, ctData);
                }
            }

            if (isSend) {
                const ksData = {};
                for (const key in kiemTraSoLieuData) {
                    if (Object.prototype.hasOwnProperty.call(kiemTraSoLieuData, key)) {
                        const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
                        ksData[pascalKey] = kiemTraSoLieuData[key];
                    }
                }
                ksData.TrangThaiGuiKetQua = trangThaiGuiKetQuaValue.daGui;
                await Put(KIEMTRASOLIEU_API.UPDATE_ITEM, kiemTraSoLieuId, ksData);
            }

            toast.success(isSend ? 'Gửi kết quả thành công!' : 'Lưu thông tin thành công!');
            await refetchChiTiets();
            refetchParent();
            if (onSaveSuccess) onSaveSuccess();
        } catch (error) {
            console.error(error);
            toast.error('Có lỗi xảy ra khi lưu kết quả.');
        } finally {
            setSaving(false);
            setShowConfirm(false);
        }
    };

    const handleOpenConfirm = () => {
        setShowConfirm(true);
    };

    if (isLoading) return <div className="text-center p-3"><Spinner animation="border" variant="primary" /></div>;

    const parsedTrangThai = kiemTraSoLieuData?.trangThaiGuiKetQua;
    const isDaGui = parsedTrangThai === 'DaGui' || parsedTrangThai === 1 || parsedTrangThai === trangThaiGuiKetQuaValue.daGui;

    return (
        <Card className="mb-3 border-info shadow-sm">
            <Card.Header className="bg-info text-white font-weight-bold">Điều kiện quyết định</Card.Header>
            <Card.Body>
                <div className="table-responsive">
                    <Table striped bordered hover className="align-middle mb-0">
                            <thead className="bg-light text-center">
                                <tr>
                                    <th className="text-center font-weight-bold" width="60">TT</th>
                                    <th className="text-center font-weight-bold">Điều kiện</th>
                                    <th className="text-center font-weight-bold" width="250">Đánh giá</th>
                                </tr>
                            </thead>
                            <tbody>
                                {chiTiets.map((cb, idx) => {
                                    const label = objectLoaiDieuKien.find(x => x.value === cb.loaiDieuKien)?.label || cb.loaiDieuKien;
                                    return (
                                        <tr key={idx}>
                                            <td className="text-center align-middle" style={{ fontWeight: 600, fontSize: '15px' }}>{idx + 1}</td>
                                            <td className="align-middle" style={{ fontWeight: 600, fontSize: '15px' }}>{label}</td>
                                            <td className="align-middle">
                                                {readOnly || isUserView ? (
                                                    <span className="text-dark" style={{ fontSize: '15px', fontWeight: 600 }}>
                                                        {objectLoaiDanhGia.find(opt => opt.value === cb.loaiDanhGia)?.label || <span className="text-muted font-italic" style={{ fontWeight: 'normal' }}>Chưa cập nhật</span>}
                                                    </span>
                                                ) : (
                                                    <Form.Control 
                                                        as="select" 
                                                        value={cb.loaiDanhGia || ''} 
                                                        onChange={(e) => handleChiTietChange(idx, 'loaiDanhGia', e.target.value || null)}
                                                        disabled={readOnly || isUserView}
                                                        className={readOnly || isUserView ? "bg-white" : ""}
                                                    >
                                                        <option value="">Chọn đánh giá</option>
                                                        {objectLoaiDanhGia.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </Form.Control>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    <Row className="mt-3">
                        <Col md={12}>
                            {readOnly || isUserView ? (
                                <FieldDisplayFile label="File quyết định" value={fileQuyetDinh} />
                            ) : (
                                <Form.Group>
                                    <DocumentInput
                                        labelTitle="File quyết định"
                                        value={fileQuyetDinh}
                                        onChangeProp={(url) => setFileQuyetDinh(url)}
                                        readOnly={readOnly || isUserView}
                                    />
                                </Form.Group>
                            )}
                        </Col>
                    </Row>
            </Card.Body>

            {!readOnly && !isUserView && (
                <Card.Footer className="bg-white card-footer-sticky">
                    <div className="d-flex justify-content-end pr-4">
                        <Button variant="success" onClick={() => handleSave(false)} disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" className="mr-1" /> : <i className="fas fa-save mr-1"></i>}
                            Lưu thông tin
                        </Button>
                        <Button variant="primary" className="ml-2" onClick={handleOpenConfirm} disabled={saving}>
                            {saving ? <><Spinner size="sm" animation="border" className="mr-2" /> Đang gửi...</> : <><i className="fas fa-paper-plane mr-2"></i> {isDaGui ? 'Gửi lại kết quả' : 'Gửi kết quả'}</>}
                        </Button>
                    </div>
                </Card.Footer>
            )}

            <ConfirmModal
                isOpen={showConfirm}
                title="Xác nhận gửi kết quả"
                content={
                    <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                        Lưu ý: Khi gửi thông tin này, kết quả kiểm tra số liệu sẽ được gửi tới nghiên cứu sinh. Bạn có chắc chắn muốn gửi điều kiện quyết định này không?
                    </div>
                }
                labelAccept="Đồng ý gửi"
                handleAccept={() => handleSave(true)}
                handleCancel={() => setShowConfirm(false)}
            />
        </Card>
    );
};

export default DieuKienQuyetDinhForm;
